import {
  getDate as getDay,
  getMinutes,
  getMonth,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isSameHour,
  isSameMonth,
  isSameYear,
  parse,
  setDate as setDay,
  setHours,
  setMinutes,
  setMonth,
  setYear,
} from 'date-fns';
import { omit } from 'lodash';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useMemo,
} from 'react';

import Field from 'components/Field';
import SelectField from 'components/SelectField';
import {
  get24from12,
  getHourOptions,
  getHours12,
  getMeridiem,
  getMeridiemOptions,
  getMinuteOptions,
  getDayOptions,
  getMonthOptions,
  getYearOptions,
  isPM,
  isSameMeridiem,
} from 'lib/dateUtils';

const styles = {
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    // marginTop: 10,
    marginBottom: 15,
  },
  select: {
    marginRight: 10,
  },
  dateOrTime: {
    marginRight: '2%',
    width: '32%',
  },
  dateOrTimeLast: {
    width: '32%',
    marginRight: null,
  },
  datetime: {
    marginRight: '2%',
    width: '15%',
  },
  datetimeLast: {
    width: '15%',
    marginRight: null,
  },
};

const DatetimeField = ({
  disabled,
  errorText,
  fullWidth,
  maxDate,
  minDate,
  onChange,
  showDate = true,
  showTime = true,
  value,
  ...props
}) => {
  const date = useMemo(
    () => {
      /**
       * convert incoming value to date, or else use current date
       *
       * @type {Date}
       */
      let dt = value
        ? parse(value)
        : new Date();
      if (minDate && isBefore(dt, minDate)) {
        dt = minDate;
      }
      if (maxDate && isAfter(dt, maxDate)) {
        dt = maxDate;
      }
      return dt;
    },
    [
      maxDate,
      minDate,
      value,
    ],
  );
  const onDayChange = useCallback(
    (e, idx, day) => onChange(e, setDay(date, day)),
    [
      date,
      onChange,
    ],
  );
  const onHoursChange = useCallback(
    (e, idx, hours12) => onChange(
      e,
      setHours(
        date,
        get24from12(
          hours12,
          getMeridiem(date),
        ),
      ),
    ),
    [
      date,
      onChange,
    ],
  );
  const onMeridiemChange = useCallback(
    (e, idx, meridiem) => onChange(
      e,
      setHours(
        date,
        get24from12(
          getHours12(date),
          meridiem,
        ),
      ),
    ),
    [
      date,
      onChange,
    ],
  );
  const onMinutesChange = useCallback(
    (e, idx, minutes) => onChange(
      e,
      setMinutes(
        date,
        minutes,
      ),
    ),
    [
      date,
      onChange,
    ],
  );
  const onMonthChange = useCallback(
    (e, idx, month) => onChange(
      e,
      setMonth(
        date,
        month,
      ),
    ),
    [
      date,
      onChange,
    ],
  );
  const onYearChange = useCallback(
    (e, idx, year) => onChange(
      e,
      setYear(
        date,
        year,
      ),
    ),
    [
      date,
      onChange,
    ],
  );
  const meridiemOptions = useMemo(
    () => {
      let options = getMeridiemOptions();
      if (minDate && isSameDay(date, minDate) && isPM(minDate)) {
        options = omit(options, 'am');
      }
      if (maxDate && isSameDay(date, maxDate) && !isPM(maxDate)) {
        options = omit(options, 'pm');
      }
      return options;
    },
    [
      date,
      maxDate,
      minDate,
    ],
  );
  const fullWidthStyle = useMemo(
    () => (showTime
      ? styles.datetime
      : styles.dateOrTime),
    [showTime],
  );
  const fullWidthLastStyle = useMemo(
    () => (showTime
      ? styles.datetime
      : styles.dateOrTimeLast),
    [showTime],
  );
  return (
    <Field
      {...props}
      floatingLabelFixed
      fullWidth
    >
      <div style={styles.container}>
        {useMemo(
          () => showDate && (
            <Fragment>
              <SelectField
                disabled={disabled}
                onChange={onMonthChange}
                options={getMonthOptions(
                  minDate && isSameYear(date, minDate)
                    ? getMonth(minDate)
                    : undefined,
                  maxDate && isSameYear(date, maxDate)
                    ? getMonth(maxDate)
                    : undefined,
                )}
                showEmpty={false}
                style={fullWidth
                  ? fullWidthStyle
                  : null}
                value={getMonth(date)
                  .toString()}
              />
              <SelectField
                disabled={disabled}
                options={getDayOptions(
                  minDate && isSameMonth(date, minDate)
                    ? getDay(minDate)
                    : undefined,
                  maxDate && isSameMonth(date, maxDate)
                    ? getDay(maxDate)
                    : undefined,
                  date,
                )}
                onChange={onDayChange}
                showEmpty={false}
                style={fullWidth
                  ? fullWidthStyle
                  : null}
                value={getDay(date)
                  .toString()}
              />
              <SelectField
                disabled={disabled}
                onChange={onYearChange}
                options={getYearOptions(
                  minDate
                    ? getYear(minDate)
                    : getYear(date) - 98,
                  maxDate
                    ? getYear(maxDate)
                    : getYear(date) + 1,
                )}
                showEmpty={false}
                style={fullWidth
                  ? fullWidthLastStyle
                  : null}
                value={getYear(date)
                  .toString()}
              />
            </Fragment>
          ),
          [
            date,
            disabled,
            fullWidth,
            fullWidthLastStyle,
            fullWidthStyle,
            maxDate,
            minDate,
            onDayChange,
            onMonthChange,
            onYearChange,
            showDate,
          ],
        )}
        {useMemo(
          () => showTime && (
            <Fragment>
              <SelectField
                disabled={disabled}
                options={getHourOptions(
                  minDate && isSameMeridiem(date, minDate)
                    ? getHours12(minDate)
                    : undefined,
                  maxDate && isSameMeridiem(date, maxDate)
                    ? getHours12(maxDate)
                    : undefined,
                )}
                onChange={onHoursChange}
                showEmpty={false}
                style={fullWidth
                  ? fullWidthStyle
                  : null}
                value={getHours12(date)
                  .toString()}
              />
              <SelectField
                disabled={disabled}
                onChange={onMinutesChange}
                options={getMinuteOptions(
                  minDate && isSameHour(date, minDate)
                    ? getMinutes(minDate)
                    : undefined,
                  maxDate && isSameHour(date, maxDate)
                    ? getMinutes(maxDate)
                    : undefined,
                )}
                showEmpty={false}
                style={fullWidth
                  ? fullWidthStyle
                  : null}
                value={getMinutes(date)
                  .toString()}
              />
              <SelectField
                disabled={disabled}
                onChange={onMeridiemChange}
                options={meridiemOptions}
                showEmpty={false}
                style={fullWidth
                  ? fullWidthLastStyle
                  : null}
                value={getMeridiem(date)}
              />
            </Fragment>
          ),
          [
            date,
            disabled,
            fullWidth,
            fullWidthLastStyle,
            fullWidthStyle,
            maxDate,
            meridiemOptions,
            minDate,
            onHoursChange,
            onMeridiemChange,
            onMinutesChange,
            showTime,
          ],
        )}
      </div>
    </Field>
  );
};

DatetimeField.propTypes = {
  ...Field.propTypes,
  disabled: PropTypes.bool,
  errorText: PropTypes.string,
  fullWidth: PropTypes.bool,
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  showDate: PropTypes.bool,
  showTime: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.string,
  ]),
};

export default DatetimeField;
